// CNC 파트너 — 기공소 (연결된 기공소 목록 · 초대 링크 · 기공소별 월 물량) (2026-10-23, UI 구조 단계)
import React, { useState } from 'react';
import { Building2, Copy, TrendingUp, TrendingDown, UserCog } from 'lucide-react';
import PageHeader from '../common/PageHeader';
import { SAMPLE_LABS, SAMPLE_SALES } from './partnerSample';
import { useIsMobile, fmtMd, SampleNotice, ui } from './partnerUi';

// 제조사 직접 유치 링크 — 기공소 가입 화면으로 가며 mfr = 이 제조사(가입 시 inviteMfr 로 저장). 지금은 샘플 id
const INVITE_URL = 'https://dental-lab-system.web.app/signup?type=lab&mfr=PARTNER-SAMPLE';

function PartnerLabs() {
    const mobile = useIsMobile();
    // 유치 귀속(문헌 조사 10-23 권고): 처음 넣은 추천코드 기준·기록 변경 불가·재배정은 제조사가 사유를 남겨야 함
    const [via, setVia] = useState(() => Object.fromEntries(SAMPLE_LABS.map((l) => [l.id, { ...l.via, log: [] }])));
    const reassign = (l) => {
        const options = ['direct', ...SAMPLE_SALES.map((x) => x.id)];
        const labels = ['직접 유치', ...SAMPLE_SALES.map((x) => x.name)];
        const pick = window.prompt(`누구 몫으로 바꿀까요? 번호 입력\n${labels.map((t, i) => `${i + 1}. ${t}`).join('\n')}`);
        const idx = parseInt(pick, 10) - 1;
        if (!(idx >= 0 && idx < options.length)) return;
        const reason = window.prompt('바꾸는 사유를 적어 주세요 (기록으로 남습니다)');
        if (!reason || !reason.trim()) { alert('사유가 있어야 바꿀 수 있습니다.'); return; }
        const to = options[idx];
        setVia((p) => ({ ...p, [l.id]: { ...(to === 'direct' ? { type: 'direct' } : { type: 'sales', salesId: to }), log: [...p[l.id].log, { at: new Date(), to: labels[idx], reason: reason.trim() }] } }));
    };
    const total = SAMPLE_LABS.reduce((s, l) => s + l.monthCount, 0);
    const directCount = SAMPLE_LABS.filter((l) => l.via.type === 'direct').length;
    const salesOf = (id) => SAMPLE_SALES.find((x) => x.id === id) || { name: '영업업체' };


    return (
        <div>
            <PageHeader icon={Building2} title={'기공소'} subtitle={'연결된 기공소와 이번 달 물량을 봅니다.'} />
            <div style={mobile ? ui.bodyMobile : ui.body}>
                <SampleNotice />

                {/* 초대 링크 */}
                <div style={{ ...ui.card, display: 'flex', flexDirection: 'column', gap: 8 }}>
                    <span style={ui.cardTitle}>{'기공소 초대 링크'}</span>
                    <p style={ui.cardSub}>{'이 링크로 가입한 기공소는 「직접 유치」로 자동 연결됩니다. 영업업체 추천코드로 가입한 기공소는 그 영업업체 이름으로 표시됩니다.'}</p>
                    <div style={styles.inviteRow}>
                        <input style={styles.inviteInput} value={INVITE_URL} readOnly onFocus={(e) => e.target.select()} />
                        {/* 샘플 링크 — 실제 발급 전에는 복사 금지(가입 데이터에 가짜 값이 영구 저장됨, 시뮬레이션 10-23 [상]3) */}
                        <button type="button" style={{ ...ui.primaryBtn, opacity: 0.45, cursor: 'not-allowed' }} disabled title="미리보기 — 실제 링크는 서버 연결 후 발급됩니다">
                            <Copy size={14} />{'복사 (준비 중)'}
                        </button>
                    </div>
                </div>

                {/* 유치 경로 요약 */}
                <div style={styles.viaRow}>
                    <span style={styles.viaChip}>{`전체 ${SAMPLE_LABS.length}곳`}</span>
                    <span style={{ ...styles.viaChip, ...styles.viaDirect }}>{`직접 유치 ${directCount}곳`}</span>
                    <span style={{ ...styles.viaChip, ...styles.viaSales }}>{`영업업체 소개 ${SAMPLE_LABS.length - directCount}곳`}</span>
                </div>

                <p style={ui.muted}>{'귀속 규칙: 기공소가 가입할 때 처음 넣은 추천코드 기준으로 정해지고, 그 기록은 바뀌지 않습니다. 바꿔야 하면 「재배정」에 사유를 남기면 이력으로 쌓입니다. 영업업체 수수료는 귀속 기준으로 계산됩니다.'}</p>

                {/* 기공소 목록 */}
                <div style={ui.tableWrap}>
                    <table style={ui.table}>
                        <thead>
                            <tr>
                                <th style={ui.th}>{'기공소'}</th>
                                <th style={ui.th}>{'유치 경로'}</th>
                                {!mobile && <th style={ui.th}>{'코드'}</th>}
                                {!mobile && <th style={ui.th}>{'귀속일'}</th>}
                                <th style={{ ...ui.th, textAlign: 'right' }}>{'이번 달'}</th>
                                {!mobile && <th style={{ ...ui.th, textAlign: 'right' }}>{'지난달'}</th>}
                            </tr>
                        </thead>
                        <tbody>
                            {SAMPLE_LABS.map((l) => {
                                const diff = l.monthCount - l.lastMonthCount;
                                return (
                                    <tr key={l.id}>
                                        <td style={ui.td}>{l.name}</td>
                                        <td style={ui.td}>
                                            <div style={styles.viaCell}>
                                                {via[l.id].type === 'direct'
                                                    ? <span style={{ ...styles.viaChip, ...styles.viaDirect }}>{'직접 유치'}</span>
                                                    : <span style={{ ...styles.viaChip, ...styles.viaSales }}>{`${salesOf(via[l.id].salesId).name} 소개`}</span>}
                                                <button type="button" style={styles.reBtn} onClick={() => reassign(l)} title="유치 귀속 바꾸기(사유 기록)"><UserCog size={12} />{'재배정'}</button>
                                            </div>
                                            {via[l.id].log.map((g, i) => (
                                                <div key={i} style={styles.logLine}>{`${fmtMd(g.at)} → ${g.to} · 사유: ${g.reason}`}</div>
                                            ))}
                                        </td>
                                        {!mobile && <td style={{ ...ui.td, ...ui.muted }}>{l.code}</td>}
                                        {!mobile && <td style={ui.td}>{fmtMd(l.joinedAt)}</td>}
                                        <td style={ui.tdNum}>
                                            <b>{`${l.monthCount}건`}</b>
                                            {diff !== 0 && (
                                                <span style={{ ...styles.diff, color: diff > 0 ? '#047857' : '#dc2626' }}>
                                                    {diff > 0 ? <TrendingUp size={12} /> : <TrendingDown size={12} />}{Math.abs(diff)}
                                                </span>
                                            )}
                                        </td>
                                        {!mobile && <td style={{ ...ui.tdNum, color: '#64748b' }}>{`${l.lastMonthCount}건`}</td>}
                                    </tr>
                                );
                            })}
                            <tr>
                                <td style={{ ...ui.td, fontWeight: 700 }}>{'합계'}</td>
                                <td style={ui.td} />
                                {!mobile && <td style={ui.td} />}
                                {!mobile && <td style={ui.td} />}
                                <td style={{ ...ui.tdNum, fontWeight: 800 }}>{`${total}건`}</td>
                                {!mobile && <td style={ui.td} />}
                            </tr>
                        </tbody>
                    </table>
                </div>
            </div>
        </div>
    );
}

const styles = {
    inviteRow: { display: 'flex', gap: 8 },
    inviteInput: {
        flex: 1, minWidth: 0, padding: '8px 12px', border: '1px solid #cbd5e1', borderRadius: 9,
        fontSize: 13, color: '#334155', backgroundColor: '#f8fafc',
    },
    viaRow: { display: 'flex', gap: 6, flexWrap: 'wrap' },
    viaChip: { display: 'inline-block', padding: '3px 10px', borderRadius: 999, fontSize: 12, fontWeight: 700, backgroundColor: '#f1f5f9', color: '#334155', whiteSpace: 'nowrap' },
    viaDirect: { backgroundColor: '#eef2ff', color: '#3730a3' },
    viaSales: { backgroundColor: '#e0f2fe', color: '#075985' },
    viaCell: { display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' },
    reBtn: {
        display: 'inline-flex', alignItems: 'center', gap: 3, padding: '2px 7px', border: '1px solid #cbd5e1',
        borderRadius: 6, backgroundColor: '#fff', color: '#475569', fontSize: 11, fontWeight: 600, cursor: 'pointer',
    },
    logLine: { marginTop: 4, fontSize: 11, color: '#64748b' },
    diff: { display: 'inline-flex', alignItems: 'center', gap: 2, marginLeft: 6, fontSize: 12, fontWeight: 700 },
};

export default PartnerLabs;
